import { cn } from "@/utils/theme";
import { Badge, Icon } from "@elaniintc/goes-design-system";
import type { ComponentProps, ReactNode } from "react";
import type { ClassNameValue } from "tailwind-merge";

export interface IDropdownListItem {
  value: string;
  label: string | ReactNode;
  icon?: ComponentProps<typeof Icon>;
  iconNode?: ReactNode;
  badge?: ComponentProps<typeof Badge>;
  detail?: string;
  disabled?: boolean;
  type?: string;
  metaType?: string;
  className?: ClassNameValue;
  textClassName?: ClassNameValue;
  isSelected?: boolean;
  toggleSelection?: (value: IDropdownListItem) => void;
  globalType?: string;
  output_name?: string;
}

interface DropdownListItemProps extends IDropdownListItem {
  iconPosition: "left" | "right" | "hidden";
  badgePosition?: "right" | "hidden";
  showCheckbox?: boolean;
}

const DropdownListItem = ({
  iconPosition,
  badgePosition,
  toggleSelection,
  isSelected,
  showCheckbox,
  ...item
}: DropdownListItemProps) => {
  const labelClassName =
    typeof item.label === "string" && item.label.length >= 45
      ? "break-all"
      : "";

  return (
    <div
      role="menuitem"
      tabIndex={item.disabled ? -1 : 0}
      aria-disabled={item.disabled ? "true" : undefined}
      data-disabled={item.disabled ? "true" : undefined}
      className={cn(
        "rounded-lg cursor-pointer text-body relative flex items-center gap-2 py-2 px-3 text-sm outline-hidden select-none hover:bg-neutral-object-accent-silent focus:bg-accent focus:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50",
        isSelected
          ? "bg-surface-action-accent hover:bg-surface-action-accent-hover"
          : "hover:bg-neutral-object-accent-silent",
        item.disabled
          ? "pointer-events-none opacity-50 cursor-not-allowed"
          : "cursor-pointer hover:bg-neutral-object-accent-silent focus:bg-accent focus:text-accent-foreground",
      )}
      onClick={() => {
        if (!item.disabled && toggleSelection) {
          toggleSelection(item);
        }
      }}
      onKeyDown={(e) => {
        if (
          (e.key === "Enter" || e.key === " ") &&
          !item.disabled &&
          toggleSelection
        ) {
          e.preventDefault();
          toggleSelection(item);
        }
      }}
    >
      <div className={cn("w-full flex items-center gap-3", item.className)}>
        {(() => {
          const renderedIcon = item.icon ? (
            <Icon {...item.icon} />
          ) : (
            (item.iconNode ?? null)
          );
          return iconPosition?.toLowerCase() === "left" &&
            iconPosition?.toLowerCase() !== "hidden"
            ? renderedIcon
            : null;
        })()}
        <div className="flex-1 min-w-0 gap-2 truncate">
          <p
            className={cn(
              "font-semibold",
              isSelected ? "text-action" : "text-body",
              item.detail ? "" : "truncate",
              item.textClassName,
              labelClassName,
            )}
          >
            {item.label}
          </p>
          <span className="font-normal text-body-quiet truncate">
            {item?.metaType || item.detail}
          </span>
        </div>
        {item.badge && badgePosition?.toLowerCase() !== "hidden" && (
          <Badge
            size="sm"
            shape={"pill"}
            {...item.badge}
            variant={item.badge.variant ?? "neutral"}
            className={cn("shrink-0 ml-auto", item.badge.className)}
          ></Badge>
        )}
        {(() => {
          const renderedIcon = item.icon ? (
            <Icon {...item.icon} />
          ) : (
            (item.iconNode ?? null)
          );
          return iconPosition?.toLowerCase() === "right" &&
            iconPosition?.toLowerCase() !== "hidden"
            ? renderedIcon
            : null;
        })()}
        {showCheckbox && (
          <div className="shrink-0 ml-auto">
            {isSelected ? (
              <Icon
                type="fas"
                name="circle-check"
                className="text-action text-lg"
              />
            ) : (
              <div className="size-4.5 rounded-full border-2 border-neutral-quiet" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export { DropdownListItem };
